import { MEDIA_TYPE_3D_MODEL } from './model-3d.js';
import {
	dispatchProductMedia3dModelInteracted,
	dispatchProductMedia3dViewerLoadFailed,
	dispatchProductMedia3dViewerLoaded,
} from './product-media-3d-viewer.js';

/** Matches legacy product_images hero video area (`product_images.scss`). */
export const HERO_3D_MODEL_MIN_HEIGHT_PX = 260;

/**
 * @param {string} modelUrl
 * @returns {{ modelUrl: string, mediaType: string }}
 */
export function buildProductMedia3dMetricDetail(modelUrl) {
	return {
		modelUrl,
		mediaType: MEDIA_TYPE_3D_MODEL,
	};
}

/**
 * @param {{ modelUrl: string, mediaType: string }} metricDetail
 */
export function handleProductMedia3dViewerLoaded(metricDetail) {
	dispatchProductMedia3dViewerLoaded(metricDetail);
}

/**
 * @param {{ modelUrl: string, mediaType: string }} metricDetail
 */
export function handleProductMedia3dViewerLoadFailed(metricDetail) {
	dispatchProductMedia3dViewerLoadFailed(metricDetail);
}

/**
 * @param {{ modelUrl: string, mediaType: string }} metricDetail
 * @param {Event} event
 */
export function handleProductMedia3dModelInteracted(metricDetail, event) {
	if (event?.detail?.source === 'user-interaction') {
		dispatchProductMedia3dModelInteracted(metricDetail);
	}
}

/**
 * @param {HTMLElement} modelViewerElement
 * @param {{ modelUrl: string, mediaType: string }} metricDetail
 * @param {{ onViewerLoadFailed?: () => void }} [options]
 * @returns {() => void}
 */
export function attachProductMedia3dViewerListeners(modelViewerElement, metricDetail, options = {}) {
	const onViewerLoaded = () => handleProductMedia3dViewerLoaded(metricDetail);
	const onViewerLoadFailed = () => {
		handleProductMedia3dViewerLoadFailed(metricDetail);
		options.onViewerLoadFailed?.();
	};
	const onModelInteracted = (event) => handleProductMedia3dModelInteracted(metricDetail, event);

	modelViewerElement.addEventListener('load', onViewerLoaded);
	modelViewerElement.addEventListener('error', onViewerLoadFailed);
	modelViewerElement.addEventListener('camera-change', onModelInteracted);

	if (modelViewerElement.loaded === true) {
		handleProductMedia3dViewerLoaded(metricDetail);
	}

	return () => {
		modelViewerElement.removeEventListener('load', onViewerLoaded);
		modelViewerElement.removeEventListener('error', onViewerLoadFailed);
		modelViewerElement.removeEventListener('camera-change', onModelInteracted);
	};
}

/**
 * @param {HTMLElement} containerElement
 * @param {{ modelUrl: string, mediaType: string }} metricDetail
 * @param {() => boolean} isCancelled
 * @param {{ onViewerLoadFailed?: () => void }} [options]
 * @returns {() => void}
 */
export function watchAndAttachProductMedia3dViewerListeners(containerElement, metricDetail, isCancelled, options = {}) {
	let removeListeners = () => {};
	/** @type {MutationObserver | null} */
	let observer = null;

	function tryAttach() {
		if (isCancelled()) {
			return true;
		}

		const modelViewerElement = containerElement.querySelector('model-viewer');
		if (modelViewerElement) {
			removeListeners = attachProductMedia3dViewerListeners(modelViewerElement, metricDetail, options);
			return true;
		}

		return false;
	}

	if (tryAttach()) {
		return () => {
			removeListeners();
		};
	}

	if (typeof MutationObserver === 'function') {
		observer = new MutationObserver(() => {
			if (tryAttach() && observer !== null) {
				observer.disconnect();
				observer = null;
			}
		});
		observer.observe(containerElement, { childList: true, subtree: true });
	}

	return () => {
		if (observer !== null) {
			observer.disconnect();
		}
		removeListeners();
	};
}
